import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/Layout';
import axios from 'axios';
import { Search, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_BADGES = {
  advisor:    { bg: '#E8F5E0', text: '#2D5016', label: 'Advisor' },
  spouse:     { bg: '#FDE8E4', text: '#9B3B22', label: 'Spouse' },
  kids:       { bg: '#E4EEF7', text: '#1A4A7A', label: 'Kids' },
  parents:    { bg: '#EDE4F7', text: '#5A2A8A', label: 'Parents' },
  retirement: { bg: '#FDF3DC', text: '#7A5010', label: 'Retirement' },
};

const CATEGORIES = ['All', 'Advisor', 'Spouse', 'Kids', 'Parents', 'Retirement'];

const SCENARIO_DATA = {
  'A Conversation with a Financial Advisor': {
    category: 'advisor',
    description: "Step into a real advisory meeting. Learn how to ask the right questions, understand the recommendations you're given, and leave feeling confident \u2014 not confused.",
    photo: 'https://images.pexels.com/photos/5816285/pexels-photo-5816285.jpeg?auto=compress&cs=tinysrgb&w=600',
    difficulty: 1,
  },
  'Budget Disagreement': {
    category: 'spouse',
    description: "Your spouse wants to revisit last month's spending. Practice staying calm, listening openly, and finding a path forward together without letting tension take over.",
    photo: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=600&q=80',
    difficulty: 2,
  },
  'Major Purchase Decision': {
    category: 'spouse',
    description: "A big purchase is on the table and you're not sure about it. Practice how to raise concerns, explore trade-offs, and reach a decision you both feel good about.",
    photo: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=600&q=80',
    difficulty: 2,
  },
  'Financial Goals Alignment': {
    category: 'spouse',
    description: "You and your partner have different visions for your future. Practice the art of collaborative goal-setting \u2014 finding shared priorities without compromising what matters most to each of you.",
    photo: 'https://images.unsplash.com/photo-1758686254415-9348b5b5df01?w=600&q=80',
    difficulty: 2,
  },
  'Allowance & Saving': {
    category: 'kids',
    description: "Your child wants something now \u2014 not later. Practice explaining delayed gratification in a way that clicks for a young mind, building habits that will last a lifetime.",
    photo: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=600&q=80',
    difficulty: 1,
  },
  'College Savings Plan': {
    category: 'kids',
    description: "Talking about college costs doesn't have to feel scary. Practice framing long-term education planning in ways that feel exciting and achievable for your family.",
    photo: 'https://images.unsplash.com/photo-1632834380561-d1e05839a33a?w=600&q=80',
    difficulty: 2,
  },
  'First Job Money Talk': {
    category: 'kids',
    description: "Your teenager just got their first paycheck. Practice the conversation that sets them up for life \u2014 budgeting, saving, and the basics of earning your own way.",
    photo: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80',
    difficulty: 1,
  },
  'Supporting Aging Parents': {
    category: 'parents',
    description: "Care costs are real, and the conversation is sensitive. Practice navigating the financial reality of supporting a parent while preserving dignity and the relationship.",
    photo: 'https://images.unsplash.com/photo-1581579438747-1dc8d17bbce4?w=600&q=80',
    difficulty: 3,
  },
  'Inheritance Talk': {
    category: 'parents',
    description: "Estate planning is one of the hardest topics families avoid. Practice discussing wills, assets, and long-term wishes with sensitivity \u2014 so nothing important is left unsaid.",
    photo: 'https://images.unsplash.com/photo-1604881990409-b9f246db39da?w=600&q=80',
    difficulty: 3,
  },
  'Healthcare Costs Discussion': {
    category: 'parents',
    description: "Rising costs are putting real pressure on families. Practice talking with a parent about options, insurance gaps, and how to plan together without anyone feeling like a burden.",
    photo: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&q=80',
    difficulty: 3,
  },
  'Retirement Dreams': {
    category: 'retirement',
    description: "What does retirement actually look like \u2014 and can you afford it? Practice getting specific about the life you want and mapping out a shared financial vision with your partner.",
    photo: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600&q=80',
    difficulty: 1,
  },
  'Downsizing Discussion': {
    category: 'retirement',
    description: "Selling the family home is more than a financial decision. Practice the emotional and practical conversation about downsizing \u2014 what to keep, what to let go, and what comes next.",
    photo: 'https://images.unsplash.com/photo-1513584684374-8bab748fbf90?w=600&q=80',
    difficulty: 2,
  },
  'Social Security Timing': {
    category: 'retirement',
    description: "When to claim is one of the biggest retirement decisions you'll make. Practice talking through the trade-offs \u2014 early vs. delayed \u2014 so you understand what's really at stake.",
    photo: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&q=80',
    difficulty: 2,
  },
};

function DifficultyDots({ level }) {
  return (
    <span className="inline-flex items-center gap-[3px]">
      {[1, 2, 3].map((i) => (
        <span
          key={i}
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: i <= level ? '#C47A3A' : '#D4C9B8',
          }}
        />
      ))}
    </span>
  );
}

function ScenarioCard({ scenario, enhancement, onClick }) {
  const badge = CATEGORY_BADGES[enhancement.category] || CATEGORY_BADGES.advisor;

  return (
    <div
      onClick={onClick}
      data-testid={`scenario-card-${scenario.id}`}
      className="group cursor-pointer"
      style={{
        backgroundColor: '#fff',
        borderRadius: 16,
        boxShadow: '0 2px 8px rgba(26,46,26,0.06)',
        overflow: 'hidden',
        transition: 'transform 0.28s ease, box-shadow 0.28s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(26,46,26,0.12)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(26,46,26,0.06)';
      }}
    >
      {/* Photo block */}
      <div style={{ position: 'relative', height: 200, overflow: 'hidden' }}>
        <img
          src={enhancement.photo}
          alt={scenario.title}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.4s ease',
          }}
          className="group-hover:scale-[1.04]"
          loading="lazy"
        />
        {/* Gradient overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, transparent 40%, rgba(10,25,10,0.55) 100%)',
            pointerEvents: 'none',
          }}
        />
        {/* Category badge */}
        <span
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            padding: '4px 12px',
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 500,
            fontFamily: "'DM Sans', sans-serif",
            letterSpacing: '0.02em',
            backgroundColor: badge.bg,
            color: badge.text,
          }}
          data-testid={`badge-${enhancement.category}`}
        >
          {badge.label}
        </span>
      </div>

      {/* Card body */}
      <div style={{ padding: '1.25rem 1.4rem 1.5rem' }}>
        <h3
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.15rem',
            fontWeight: 700,
            color: '#1A2E1A',
            marginBottom: 8,
            lineHeight: 1.3,
          }}
        >
          {scenario.title}
        </h3>
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '13.5px',
            lineHeight: 1.65,
            color: '#5A5040',
            marginBottom: 16,
            fontWeight: 300,
          }}
        >
          {enhancement.description}
        </p>

        {/* Meta row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                color: '#8B7355',
                fontWeight: 400,
              }}
            >
              <Clock size={13} strokeWidth={1.8} />
              {"3\u20135 min"}
            </span>
            <DifficultyDots level={enhancement.difficulty} />
          </div>
          <button
            data-testid={`practice-btn-${scenario.id}`}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              fontWeight: 500,
              padding: '6px 16px',
              borderRadius: 20,
              border: 'none',
              backgroundColor: '#2D5016',
              color: '#F7F4EF',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1A3009')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2D5016')}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            Practice &rarr;
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ScenarioLibrary() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    loadScenarios();
  }, []);

  const loadScenarios = async () => {
    try {
      const response = await axios.get(`${API}/scenarios`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setScenarios(response.data);
    } catch {
      try {
        await axios.post(`${API}/seed`);
        const response = await axios.get(`${API}/scenarios`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setScenarios(response.data);
      } catch (seedError) {
        console.error('Failed to seed:', seedError);
      }
    }
    setLoading(false);
  };

  const enrichedScenarios = useMemo(() => {
    return scenarios.map((s) => ({
      ...s,
      enhancement: SCENARIO_DATA[s.title] || {
        category: s.category,
        description: s.description,
        photo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&q=80',
        difficulty: 2,
      },
    }));
  }, [scenarios]);

  const filtered = useMemo(() => {
    let list = enrichedScenarios;

    if (activeCategory !== 'All') {
      const cat = activeCategory.toLowerCase();
      list = list.filter((s) => s.enhancement.category === cat);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (s) =>
          s.title.toLowerCase().includes(q) ||
          s.enhancement.description.toLowerCase().includes(q)
      );
    }

    // Pin advisor to top
    list.sort((a, b) => {
      const aPin = a.category === 'advisor' ? 0 : 1;
      const bPin = b.category === 'advisor' ? 0 : 1;
      return aPin - bPin;
    });

    return list;
  }, [enrichedScenarios, activeCategory, searchQuery]);

  if (loading) {
    return (
      <Layout>
        <div
          style={{
            minHeight: '100vh',
            backgroundColor: '#F7F4EF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              border: '3px solid #D4C9B8',
              borderTopColor: '#2D5016',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div
        data-testid="scenario-library"
        style={{
          minHeight: '100vh',
          backgroundColor: '#F7F4EF',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '3rem 1.5rem 4rem' }}>
          {/* Header */}
          <div style={{ marginBottom: '2rem' }}>
            <p
              style={{
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#8B7355',
                marginBottom: 8,
                fontFamily: "'DM Sans', sans-serif",
              }}
              data-testid="eyebrow-label"
            >
              Practice Conversations
            </p>
            <h1
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: '2rem',
                fontWeight: 700,
                color: '#1A2E1A',
                margin: 0,
                lineHeight: 1.2,
              }}
              data-testid="page-title"
            >
              Choose a scenario to practice
            </h1>
          </div>

          {/* Controls row */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '2rem',
              flexWrap: 'wrap',
            }}
          >
            {/* Search */}
            <div style={{ position: 'relative', flex: '1 1 280px', minWidth: 200 }}>
              <Search
                size={16}
                strokeWidth={2}
                style={{
                  position: 'absolute',
                  left: 14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#8B7355',
                  pointerEvents: 'none',
                }}
              />
              <input
                type="text"
                placeholder="Search scenarios..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="scenario-search"
                style={{
                  width: '100%',
                  padding: '10px 16px 10px 40px',
                  borderRadius: 24,
                  border: '1px solid #D4C9B8',
                  backgroundColor: '#fff',
                  fontSize: 14,
                  fontFamily: "'DM Sans', sans-serif",
                  color: '#1A2E1A',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                }}
                onFocus={(e) => (e.target.style.borderColor = '#2D5016')}
                onBlur={(e) => (e.target.style.borderColor = '#D4C9B8')}
              />
            </div>

            {/* Category pills */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
              data-testid="category-filters"
            >
              {CATEGORIES.map((cat) => {
                const isActive = activeCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    data-testid={`filter-${cat.toLowerCase()}`}
                    style={{
                      padding: '7px 16px',
                      borderRadius: 20,
                      border: isActive ? '1px solid #2D5016' : '1px solid #D4C9B8',
                      backgroundColor: isActive ? '#2D5016' : '#fff',
                      color: isActive ? '#F7F4EF' : '#5A4A35',
                      fontSize: 13,
                      fontWeight: 500,
                      fontFamily: "'DM Sans', sans-serif",
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Card Grid */}
          {filtered.length > 0 ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '1.5rem',
              }}
              data-testid="scenario-grid"
            >
              {filtered.map((scenario) => (
                <ScenarioCard
                  key={scenario.id}
                  scenario={scenario}
                  enhancement={scenario.enhancement}
                  onClick={() => navigate(`/scenario/${scenario.id}`)}
                />
              ))}
            </div>
          ) : (
            <div
              style={{
                textAlign: 'center',
                padding: '4rem 1rem',
                color: '#8B7355',
              }}
            >
              <Search
                size={40}
                strokeWidth={1.2}
                style={{ margin: '0 auto 1rem', color: '#D4C9B8' }}
              />
              <h3
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: '#1A2E1A',
                  marginBottom: 8,
                }}
              >
                No scenarios found
              </h3>
              <p style={{ fontSize: 14, color: '#8B7355' }}>
                Try adjusting your search or filters
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
